import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useMemo,
  useCallback,
} from "react";
import { toast } from "react-toastify";
import endpoints from "../literals/endpoints";

const MarketContext = createContext(null);

export const MarketProvider = ({ children }) => {
  const [packages, setPackages] = useState([]);
  const [addons, setAddons] = useState([]);
  const [orgData, setOrgData] = useState(null);
  const [currentExtraAddons, setCurrentExtraAddons] = useState(0);
  const [currentAddons, setCurrentAddons] = useState(0);
  const [currentPlan, setCurrentPlan] = useState(null);
  const [selected, setSelected] = useState(null);
  const [autoDiscount, setAutoDiscount] = useState(0);
  const [manualDiscount, setManualDiscount] = useState(0);
  const [isLoaded, setLoaded] = useState(false);
  const [isActive, setIsActive] = useState(false);

  const fetchPackages = useCallback(async () => {
    const selectedOrg = localStorage.getItem("selectedOrg");
    try {
      const res = await fetch(endpoints.getPackages(selectedOrg), {
        credentials: "include",
      });
      if (!res.ok) throw new Error("Errore nella chiamata API");
      const data = await res.json();
      setPackages(data);
      setAddons(data.filter((elem) => elem.label === "Mezzo"));
    } catch (err) {
      console.error(err);
      toast.error("Errore nel caricamento dei pacchetti");
    }
  }, []);

  const fetchOrgData = useCallback(async () => {
    const selectedOrg = localStorage.getItem("selectedOrg");
    if (!selectedOrg) return;
    try {
      const res = await fetch(endpoints.getOrg(selectedOrg), {
        credentials: "include",
      });
      if (!res.ok)
        throw new Error("Errore nel recupero dell’organizzazione");
      const data = await res.json();
      setOrgData(data[0]);
      setIsActive(new Date(data[0].expiry_date) > new Date());
    } catch (err) {
      console.error(err);
      toast.error("Errore nel caricamento dati organizzazione");
    }
  }, []);

  useEffect(() => {
    fetchPackages();
    fetchOrgData();
  }, [fetchPackages, fetchOrgData]);

  useEffect(() => {
    if (!orgData) return;

    if (!isActive) {
      setCurrentPlan(null);
      setCurrentAddons(0);
      return;
    }

    const plan = packages.find((p) => p.id === orgData.licence) ?? null;
    setCurrentPlan(plan);

    const baseVehicles = (selected?.vehicles ?? plan?.vehicles) ?? 0;
    const addonsCount = Math.max(0, (orgData.vehicles ?? 0) - baseVehicles);
    setCurrentAddons(addonsCount);
  }, [orgData, selected, packages, isActive]);

  useEffect(() => {
    if (currentPlan?.price_amount != null && selected?.price_amount != null) {
      setLoaded(true);
    } else {
      setLoaded(false);
    }
  }, [currentPlan, selected]);

  const handleSelect = useCallback((pkg) => {
    setSelected((prevSelected) => {
      const isSame = prevSelected?.stripe_price_id === pkg.stripe_price_id;
      if (isSame) {
        setAutoDiscount(0);
        setManualDiscount(0);
        return null;
      }
      return pkg;
    });
  }, []);

  const ponderatePriceForTime = useCallback(
    (price) => {
      if (!orgData?.expiry_date) return 0;
      const today = new Date();
      const expiry = new Date(orgData.expiry_date);
      const days = Math.max(
        0,
        Math.ceil((expiry - today) / (1000 * 60 * 60 * 24))
      );
      return Number((price / 365) * days);
    },
    [orgData?.expiry_date]
  );

  const calculateFinalPrice = useCallback(() => {
    if (!isActive && selected) return Number(selected.price_amount);
    if (!currentPlan || !selected) return 0;

    if (currentPlan.price_amount < selected.price_amount) {
      return Number(
        selected.price_amount - ponderatePriceForTime(currentPlan.price_amount)
      );
    }
    return Number(selected.price_amount);
  }, [isActive, selected, currentPlan, ponderatePriceForTime]);

  const calculateFinalPriceAddon = useCallback(
    (price) => {
      if (!isActive || (selected && selected?.price_amount))
        return price * currentExtraAddons;

      return Number(ponderatePriceForTime(currentExtraAddons * price));
    },
    [isActive, selected, currentExtraAddons, ponderatePriceForTime]
  );

  const calculateDiscountAddon = useCallback(
    (price) => {
      if (!isActive) return 0;
      if (!orgData || !currentPlan) return 0;

      const oldVehicles = (orgData.vehicles ?? 0) - (currentPlan.vehicles ?? 0);

      if (
        selected?.price_amount &&
        currentPlan.price_amount < selected.price_amount
      ) {
        return ponderatePriceForTime(
          price * (oldVehicles - (currentAddons > 0 ? currentAddons : 0))
        );
      }
      return 0;
    },
    [isActive, orgData, currentPlan, selected, currentAddons, ponderatePriceForTime]
  );

  const upgradeDiscount = useCallback(() => {
    if (!isActive) return 0;
    if (selected && selected?.id > currentPlan?.id) {
      return selected.price_amount - calculateFinalPrice();
    }
    return 0;
  }, [isActive, selected, currentPlan, calculateFinalPrice]);

  const calculateNet = useCallback(() => {
    if (!addons[0]) return 0;
    const res =
      calculateFinalPrice() +
      calculateFinalPriceAddon(addons[0].price_amount) -
      manualDiscount -
      calculateDiscountAddon(addons[0].price_amount);

    return Math.max(0, res);
  }, [
    addons,
    calculateFinalPrice,
    calculateFinalPriceAddon,
    manualDiscount,
    calculateDiscountAddon,
  ]);

  const isLoadedFetching = useCallback(
    () => orgData !== null && packages != null,
    [orgData, packages]
  );

  const incrementAddon = useCallback(() => {
    const tmpPlan = selected ?? currentPlan;
    if (!tmpPlan || !currentPlan) {
      setCurrentExtraAddons((prev) => prev + 1);
      return;
    }

    setCurrentExtraAddons((prev) => prev + 1);

    const nextExtra = currentExtraAddons + 1;
    const baseVehicles = tmpPlan.vehicles ?? 0;
    const currAddons = currentAddons > 0 ? currentAddons : 0;

    if (
      currentPlan.id < 4 &&
      nextExtra + baseVehicles + currAddons === packages[tmpPlan.id]?.vehicles
    ) {
      setSelected(packages.find((p) => p.id === tmpPlan.id + 1) ?? null);
      setCurrentExtraAddons(0);
    }
  }, [
    selected,
    currentPlan,
    currentExtraAddons,
    currentAddons,
    packages,
  ]);

  const decrementAddons = useCallback(() => {
    setCurrentExtraAddons((prev) => Math.max(0, prev - 1));
  }, []);

  const value = useMemo(
    () => ({
      packages: { state: packages, set: setPackages },
      addons: { state: addons, set: setAddons },
      orgData: { state: orgData, set: setOrgData },
      currentAddons: { state: currentAddons, set: setCurrentAddons },
      currentExtraAddons: {
        state: currentExtraAddons,
        increment: incrementAddon,
        decrement: decrementAddons,
      },
      currentPlan: { state: currentPlan, set: setCurrentPlan },
      selected: {
        state: selected,
        set: setSelected,
        handle: handleSelect,
      },
      discount: {
        autoDiscount: { state: autoDiscount, set: setAutoDiscount },
        manualDiscount: { state: manualDiscount, set: setManualDiscount },
        calculate: () =>
          calculateDiscountAddon(addons[0]?.price_amount || 0) +
          manualDiscount +
          upgradeDiscount(),
      },
      calculateFinalPrice,
      calculateFinalPriceAddon,
      calculateDiscountAddon,
      calculateNet,
      upgradeDiscount,
      isLoaded,
      isLoadedFetching,
      isActive,
    }),
    [
      packages,
      addons,
      orgData,
      currentAddons,
      currentExtraAddons,
      currentPlan,
      selected,
      autoDiscount,
      manualDiscount,
      isLoaded,
      isActive,
      incrementAddon,
      decrementAddons,
      handleSelect,
      calculateFinalPrice,
      calculateFinalPriceAddon,
      calculateDiscountAddon,
      calculateNet,
      upgradeDiscount,
      isLoadedFetching,
    ]
  );

  return (
    <MarketContext.Provider value={value}>{children}</MarketContext.Provider>
  );
};

export const useMarketContext = () => {
  const context = useContext(MarketContext);
  if (!context)
    throw new Error("useMarketContext must be used within MarketProvider");
  return context;
};